import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  BoltIcon,
  Cog6ToothIcon,
  SignalIcon,
  ArrowLongRightIcon,
  ArrowLongLeftIcon,
  ArrowLongDownIcon,
  ArrowLongUpIcon,
} from '@heroicons/react/24/outline';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import { useSignalkDashboardData } from '../../hooks/useSignalkDashboardData';
import {
  buildPowerFlowModel,
  buildDemoPowerFlat,
  createPowerReader,
  hasPowerData,
  listPowerPaths,
  formatWatts,
  formatDuration,
  FLOW_THRESHOLD_W,
  cellHealthLevel,
  deriveDashboardHints,
} from '../../utils/powerFlow';
import { Page, Header, Title, Subtitle, Toolbar, Badge, Btn } from '../liveboat/liveBoatStyles';

const FLOW_GREEN = theme.colors.success.main;
const FLOW_IDLE = theme.colors.border.primary;

/* ------------------------------------------------------------------ layout */

const Board = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(0, 1fr) 84px minmax(0, 1.15fr) 84px minmax(0, 1fr);
  grid-template-areas:
    'grid c1 inv c2 loads'
    '.    .  v1  .  .'
    'solar c3 batt . health'
    'start start chg chg chg';
  align-items: stretch;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'grid'
      'inv'
      'loads'
      'batt'
      'solar'
      'health'
      'start'
      'chg';
  }
`;

const Area = styled.div`
  grid-area: ${(p) => p.$area};
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

/* ------------------------------------------------------------- flow lines */

const moveH = keyframes`
  from { left: -8px; }
  to { left: calc(100% + 2px); }
`;
const moveV = keyframes`
  from { top: -8px; }
  to { top: calc(100% + 2px); }
`;

const FlowWrap = styled.div`
  grid-area: ${(p) => p.$area};
  position: relative;
  min-height: ${(p) => (p.$vertical ? '48px' : '100%')};
  min-width: 0;

  @media (max-width: 960px) {
    display: none;
  }
`;

const Line = styled.div`
  position: absolute;
  background: ${(p) => (p.$active ? FLOW_GREEN : FLOW_IDLE)};
  opacity: ${(p) => (p.$active ? 0.8 : 0.4)};
  border-radius: 2px;
  transition: background 0.6s ease, opacity 0.6s ease;
  ${(p) =>
    p.$vertical
      ? css`
          left: 50%;
          top: 0;
          bottom: 0;
          width: 3px;
          transform: translateX(-50%);
        `
      : css`
          top: 50%;
          left: 0;
          right: 0;
          height: 3px;
          transform: translateY(-50%);
        `}
`;

const Dots = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  opacity: ${(p) => (p.$active ? 1 : 0)};
  transition: opacity 0.6s ease;
`;

const Dot = styled.span`
  position: absolute;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #bbf7d0;
  box-shadow: 0 0 6px ${FLOW_GREEN};
  ${(p) =>
    p.$vertical
      ? css`
          left: 50%;
          margin-left: -4.5px;
          animation: ${moveV} ${p.$dur}s linear infinite;
        `
      : css`
          top: 50%;
          margin-top: -4.5px;
          animation: ${moveH} ${p.$dur}s linear infinite;
        `}
  animation-delay: ${(p) => p.$delay}s;
  animation-direction: ${(p) => (p.$reverse ? 'reverse' : 'normal')};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    display: none;
  }
`;

const FlowLabel = styled.span`
  position: absolute;
  font-size: 0.68rem;
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.success.light};
  background: ${theme.colors.background.primary};
  padding: 0 0.3rem;
  border-radius: 4px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  opacity: ${(p) => (p.$active ? 1 : 0)};
  transition: opacity 0.6s ease;
  ${(p) =>
    p.$vertical
      ? css`
          left: calc(50% + 10px);
          top: 50%;
          transform: translateY(-50%);
        `
      : css`
          left: 50%;
          top: calc(50% - 22px);
          transform: translateX(-50%);
        `}
`;

/** Fixed speed buckets: changing the animation duration restarts it (visible jump), so never derive it per-watt. */
function dotDuration(watts) {
  if (watts < 200) return 2.8;
  if (watts < 1000) return 1.9;
  return 1.3;
}

function FlowLine({ area, vertical = false, flow }) {
  const dir = flow?.dir || 'none';
  const active = dir !== 'none';
  const lastDir = useRef('forward');
  const lastWatts = useRef(0);
  if (active) {
    lastDir.current = dir;
    lastWatts.current = Math.abs(flow?.watts || 0);
  }
  const [bucket, setBucket] = useState(dotDuration(lastWatts.current));
  const wantBucket = dotDuration(lastWatts.current);
  useEffect(() => {
    // Change speed only when the bucket changes and at most every few seconds.
    if (wantBucket === bucket) return undefined;
    const t = setTimeout(() => setBucket(wantBucket), 3000);
    return () => clearTimeout(t);
  }, [wantBucket, bucket]);

  return (
    <FlowWrap $area={area} $vertical={vertical} aria-hidden="true">
      <Line $active={active} $vertical={vertical} />
      <Dots $active={active}>
        {[0, 1, 2].map((i) => (
          <Dot
            key={`${lastDir.current}-${bucket}-${i}`}
            $vertical={vertical}
            $reverse={lastDir.current === 'reverse'}
            $dur={bucket}
            $delay={-(bucket / 3) * i}
          />
        ))}
      </Dots>
      <FlowLabel $vertical={vertical} $active={active}>
        <W v={lastWatts.current} />
      </FlowLabel>
    </FlowWrap>
  );
}

/* ------------------------------------------------------------------ cards */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PCard = styled.section`
  position: relative;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.035) 0%, rgba(255, 255, 255, 0) 60%),
    ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-top: 3px solid ${(p) => p.$accent || theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: 0.9rem 1.1rem 1.05rem;
  min-width: 0;
  flex: 1;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
  animation: ${fadeUp} 0.45s ease both;
  transition: box-shadow 0.6s ease, border-color 0.6s ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.secondary};
  margin-bottom: 0.4rem;
  min-height: 1.6rem;
`;

const Big = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  font-size: 2.1rem;
  line-height: 1.1;
  min-height: 2.3rem;
  font-weight: ${theme.fontWeights.bold};
  color: ${(p) => p.$color || theme.colors.text.primary};
  font-variant-numeric: tabular-nums;

  small {
    font-size: 1rem;
    font-weight: ${theme.fontWeights.medium};
    color: ${theme.colors.text.secondary};
  }
  svg {
    width: 1.6rem;
    height: 1.6rem;
    align-self: center;
    margin-left: auto;
    color: ${(p) => p.$arrowColor || FLOW_GREEN};
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  padding: 0.18rem 0;
  font-variant-numeric: tabular-nums;

  b {
    color: ${theme.colors.text.primary};
    font-weight: ${theme.fontWeights.medium};
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.border.secondary};
  margin: 0.5rem 0;
`;

const Chip = styled.span`
  display: inline-block;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: ${theme.fontWeights.semibold};
  color: ${(p) => p.$color || theme.colors.text.secondary};
  background: ${(p) => p.$bg || theme.colors.background.tertiary};
`;

const Bar = styled.div`
  height: 8px;
  border-radius: 4px;
  background: ${theme.colors.background.tertiary};
  overflow: hidden;
  margin: 0.5rem 0;

  > div {
    height: 100%;
    width: ${(p) => p.$pct}%;
    background: ${(p) => p.$color || FLOW_GREEN};
    transition: width 0.6s ease;
  }
`;

const Empty = styled.div`
  color: ${theme.colors.text.disabled};
  font-size: ${theme.fontSizes.sm};
`;

const Panel = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 0.9rem 1.1rem;
  margin-bottom: 0.9rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  align-items: center;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};

  select {
    background: ${theme.colors.input.background};
    color: ${theme.colors.input.text};
    border: 1px solid ${theme.colors.input.border};
    border-radius: ${theme.borderRadius.md};
    padding: 0.35rem 0.5rem;
    margin-left: 0.5rem;
  }
`;

const Banner = styled.div`
  margin-bottom: 0.9rem;
  padding: 0.6rem 0.9rem;
  border-radius: ${theme.borderRadius.md};
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.4);
  color: ${theme.colors.warning.light};
  font-size: ${theme.fontSizes.sm};
`;

/* ------------------------------------------------------------- helpers */

/** Smoothly tweens a number toward its latest value so updates glide instead of snapping. */
function useTweened(target, duration = 900) {
  const [display, setDisplay] = useState(target);
  const cur = useRef(target);
  const raf = useRef(null);
  useEffect(() => {
    const valid = typeof target === 'number' && Number.isFinite(target);
    const reduce =
      typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!valid || reduce || typeof cur.current !== 'number' || !Number.isFinite(cur.current)) {
      cur.current = target;
      setDisplay(target);
      return undefined;
    }
    const from = cur.current;
    if (from === target) return undefined;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      cur.current = from + (target - from) * eased;
      setDisplay(cur.current);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return display;
}

/** Tweened watts → "919 W" / "1.23 kW". */
function W({ v }) {
  const t = useTweened(v);
  const f = formatWatts(t);
  return (
    <>
      {f.value} {f.unit}
    </>
  );
}

/** Tweened number with fixed decimals and optional unit. */
function N({ v, d = 0, u = '' }) {
  const t = useTweened(v);
  if (typeof t !== 'number' || !Number.isFinite(t)) return <>--</>;
  return (
    <>
      {t.toFixed(d)}
      {u ? ` ${u}` : ''}
    </>
  );
}

/** Rate-limits model updates so the UI refreshes calmly even when the WebSocket is chatty. */
function useThrottledValue(value, ms) {
  const [v, setV] = useState(value);
  const latest = useRef(value);
  const last = useRef(Date.now());
  const timer = useRef(null);
  latest.current = value;
  useEffect(() => {
    const elapsed = Date.now() - last.current;
    if (elapsed >= ms) {
      last.current = Date.now();
      setV(value);
    } else if (!timer.current) {
      timer.current = setTimeout(() => {
        timer.current = null;
        last.current = Date.now();
        setV(latest.current);
      }, ms - elapsed);
    }
  }, [value, ms]);
  useEffect(() => () => clearTimeout(timer.current), []);
  return v;
}

/** Hysteresis on flow direction: switch on above the threshold, off only below 55% of it. */
function useStableFlows(model) {
  const prev = useRef({});
  return useMemo(() => {
    const flows = {};
    Object.entries(model.flows).forEach(([key, f]) => {
      const was = prev.current[key] || 'none';
      const limit = was === 'none' ? FLOW_THRESHOLD_W : FLOW_THRESHOLD_W * 0.55;
      let dir = 'none';
      if (f.watts !== null && f.watts !== undefined) {
        if (f.watts > limit) dir = 'forward';
        else if (f.watts < -limit) dir = 'reverse';
      }
      prev.current[key] = dir;
      flows[key] = { ...f, dir };
    });
    return { ...model, flows };
  }, [model]);
}

function Watts({ w, color, arrow, arrowColor }) {
  const f = formatWatts(useTweened(w));
  const Arrow =
    arrow === 'right'
      ? ArrowLongRightIcon
      : arrow === 'left'
        ? ArrowLongLeftIcon
        : arrow === 'down'
          ? ArrowLongDownIcon
          : arrow === 'up'
            ? ArrowLongUpIcon
            : null;
  return (
    <Big $color={color} $arrowColor={arrowColor}>
      {f.value}
      <small>{f.unit}</small>
      {Arrow ? <Arrow /> : null}
    </Big>
  );
}

function PhaseRows({ phases }) {
  if (!phases?.length) return null;
  return (
    <>
      <Divider />
      {phases.map((ph) => (
        <Row key={ph.label}>
          <span>{ph.label}</span>
          <b>
            <N v={ph.volts} d={0} u="V" /> · <N v={ph.amps} d={1} u="A" /> · <W v={ph.watts} />
          </b>
        </Row>
      ))}
    </>
  );
}

function arrowFor(dir, forwardArrow, reverseArrow) {
  if (dir === 'forward') return forwardArrow;
  if (dir === 'reverse') return reverseArrow;
  return null;
}

/* ------------------------------------------------------------- cards */

function GridCard({ model }) {
  const { grid, flows } = model;
  const dir = flows.gridToInverter.dir;
  return (
    <PCard $accent={theme.colors.info.main} aria-label="Grid">
      <CardTitle>
        <span>{grid.sourceLabel}</span>
        {grid.available === false ? (
          <Chip $color={theme.colors.error.light}>No AC input</Chip>
        ) : dir === 'reverse' ? (
          <Chip $color={theme.colors.warning.light}>Feeding back</Chip>
        ) : null}
      </CardTitle>
      {grid.watts === null ? (
        <Empty>No AC input data</Empty>
      ) : (
        <>
          <Watts w={grid.watts} arrow={arrowFor(dir, 'right', 'left')} />
          {grid.phases.length === 0 && grid.hz ? <Row><span>Frequency</span><b><N v={grid.hz} d={1} u="Hz" /></b></Row> : null}
          <PhaseRows phases={grid.phases} />
        </>
      )}
    </PCard>
  );
}

function InverterCard({ inv }) {
  return (
    <PCard $accent={theme.colors.secondary.main} aria-label={inv.name}>
      <CardTitle>
        <span>{inv.name}</span>
        {inv.mode ? <Chip>{inv.mode}</Chip> : null}
      </CardTitle>
      <Row>
        <span>To load</span>
        <b><W v={inv.acOutW} /></b>
      </Row>
      <Divider />
      <Row>
        <span>AC in</span>
        <b>
          <N v={inv.acInV} d={0} u="V" /> · <N v={inv.acInA} d={1} u="A" /> · <W v={inv.acInW} />
        </b>
      </Row>
      {inv.acInLimitA !== null ? (
        <Row>
          <span>Input limit</span>
          <b><N v={inv.acInLimitA} d={0} u="A" /></b>
        </Row>
      ) : null}
      <Row>
        <span>AC out</span>
        <b>
          <N v={inv.acOutV} d={0} u="V" /> · <N v={inv.acOutA} d={1} u="A" /> · <W v={inv.acOutW} />
        </b>
      </Row>
      {inv.dcAmps !== null ? (
        <Row>
          <span>DC</span>
          <b><N v={inv.dcAmps} d={1} u="A" /></b>
        </Row>
      ) : null}
    </PCard>
  );
}

function LoadsCard({ model }) {
  const { loads, flows } = model;
  return (
    <PCard $accent={theme.colors.accent.main} aria-label="AC loads">
      <CardTitle>
        <span>AC Loads</span>
      </CardTitle>
      {loads.watts === null ? (
        <Empty>No AC output data</Empty>
      ) : (
        <>
          <Watts w={loads.watts} arrow={arrowFor(flows.inverterToLoads.dir, 'right', 'left')} />
          <PhaseRows phases={loads.phases} />
        </>
      )}
    </PCard>
  );
}

const HEALTH_META = {
  good: { label: 'Healthy', color: theme.colors.success.light, hint: 'Cells are well balanced.' },
  watch: { label: 'Watch', color: theme.colors.warning.light, hint: 'Cells are drifting apart — usually fine after a full charge.' },
  check: { label: 'Check cells', color: theme.colors.error.light, hint: 'Large cell difference — worth a look.' },
  unknown: { label: 'No data', color: theme.colors.text.secondary, hint: '' },
};

function HealthCard({ model }) {
  const h = model.health;
  if (!h.modules.length) {
    return (
      <PCard $accent={theme.colors.primary.light} aria-label="Battery health">
        <CardTitle>
          <span>Battery Health</span>
        </CardTitle>
        <Empty>This battery doesn't report cell-level data</Empty>
      </PCard>
    );
  }
  const level = cellHealthLevel(h.spreadMv, h.balanced);
  const meta = HEALTH_META[level];
  return (
    <PCard $accent={meta.color} aria-label="Battery health" title={meta.hint}>
      <CardTitle>
        <span>Battery Health</span>
        <Chip $color={meta.color}>{meta.label}</Chip>
      </CardTitle>
      <Big $color={meta.color}>
        <N v={h.spreadMv} d={0} />
        <small>mV cell spread</small>
      </Big>
      <Row>
        <span>Lowest / highest cell</span>
        <b>
          <N v={h.minCellV} d={3} /> / <N v={h.maxCellV} d={3} /> V
        </b>
      </Row>
      <Row>
        <span>Balancing</span>
        <b>{h.balanced === null ? '—' : h.balanced ? 'Balanced' : 'Balancing…'}</b>
      </Row>
      <Row>
        <span>Warmest module</span>
        <b>
          <N v={h.maxTempC} d={0} u="°C" />
        </b>
      </Row>
      {h.modules.length > 1 ? (
        <>
          <Divider />
          {h.modules.map((m) => (
            <Row key={m.id}>
              <span>{m.id}</span>
              <b>
                <N v={m.minV} d={2} />–<N v={m.maxV} d={2} /> V · <N v={m.tempC} d={0} u="°C" />
              </b>
            </Row>
          ))}
        </>
      ) : null}
    </PCard>
  );
}

function StarterCard({ model }) {
  const st = model.starter;
  if (!st) return null;
  const ch = st.charger;
  const charging = ch && ch.outW !== null && ch.outW > FLOW_THRESHOLD_W;
  return (
    <PCard $accent={theme.colors.accent.main} aria-label="Starter battery">
      <CardTitle>
        <span>Starter Battery</span>
        {ch ? (
          <Chip
            $color={charging ? theme.colors.success.light : theme.colors.text.secondary}
            title={ch.state ? `Charger mode: ${ch.state}` : undefined}
          >
            {charging ? 'Charging' : ch.state || 'Idle'}
          </Chip>
        ) : null}
      </CardTitle>
      <Big>
        <N v={st.volts} d={2} />
        <small>V</small>
      </Big>
      {ch ? (
        <>
          <Divider />
          <Row>
            <span>Charger output</span>
            <b>
              <N v={ch.outV} d={2} u="V" /> · <N v={ch.outA} d={1} u="A" />
            </b>
          </Row>
        </>
      ) : null}
    </PCard>
  );
}

const ChargerGrid = styled.div`
  display: grid;
  gap: 0.75rem 1.5rem;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;

function ChargerBlock({ d }) {
  const charging = d.outW !== null && d.outW > FLOW_THRESHOLD_W;
  const isDcdc = d.kind === 'dcdc';
  return (
    <div>
      <CardTitle>
        <span>
          {d.name}
          {isDcdc ? ' · DC-DC' : ''}
        </span>
        <Chip
          $color={charging ? theme.colors.success.light : theme.colors.text.secondary}
          title={d.state ? `Device mode: ${d.state}` : undefined}
        >
          {charging ? 'Charging' : 'Idle'}
        </Chip>
      </CardTitle>
      {isDcdc ? (
        <Row>
          <span>Input (source battery)</span>
          <b>
            <N v={d.inV} d={2} u="V" />
          </b>
        </Row>
      ) : null}
      <Row>
        <span>Output</span>
        <b>
          <N v={d.outV} d={2} u="V" /> · <N v={d.outA} d={1} u="A" /> · <W v={d.outW} />
        </b>
      </Row>
    </div>
  );
}

function ChargersCard({ model }) {
  const devices = model.chargerDevices || [];
  if (devices.length === 0) return null;
  return (
    <PCard $accent={theme.colors.info.light} aria-label="DC-DC and other chargers">
      <CardTitle>
        <span>DC-DC &amp; Other Chargers</span>
      </CardTitle>
      <ChargerGrid>
        {devices.map((d) => (
          <ChargerBlock key={d.key} d={d} />
        ))}
      </ChargerGrid>
    </PCard>
  );
}

function BatteryCard({ model }) {
  const { battery, dcSystemW } = model;
  if (!battery) {
    return (
      <PCard $accent={theme.colors.success.main} aria-label="Battery">
        <CardTitle>
          <span>Battery</span>
        </CardTitle>
        <Empty>No battery data</Empty>
      </PCard>
    );
  }
  const socColor =
    battery.socPercent === null
      ? undefined
      : battery.socPercent < 20
        ? theme.colors.error.light
        : battery.socPercent < 40
          ? theme.colors.warning.light
          : theme.colors.success.light;
  const statusColor =
    battery.status === 'Charging'
      ? theme.colors.success.light
      : battery.status === 'Discharging'
        ? theme.colors.warning.light
        : theme.colors.text.secondary;
  const ttg = formatDuration(battery.timeRemainingS);
  return (
    <PCard $accent={theme.colors.success.main} aria-label="Battery">
      <CardTitle>
        <span>Battery{battery.name ? ` · ${battery.name}` : ''}</span>
        {battery.status ? <Chip $color={statusColor}>{battery.status}</Chip> : null}
      </CardTitle>
      {battery.socPercent !== null ? (
        <>
          <Big $color={socColor}>
            <N v={battery.socPercent} d={battery.socPercent >= 99.95 ? 0 : 1} />
            <small>%</small>
          </Big>
          <Bar $pct={Math.max(0, Math.min(100, battery.socPercent))} $color={socColor}>
            <div />
          </Bar>
        </>
      ) : null}
      <Row>
        <span>Time to go</span>
        <b>{ttg || '—'}</b>
      </Row>
      <Row>
        <span>Voltage</span>
        <b><N v={battery.volts} d={2} u="V" /></b>
      </Row>
      <Row>
        <span>Current</span>
        <b><N v={battery.amps} d={1} u="A" /></b>
      </Row>
      <Row>
        <span>Power</span>
        <b>
          <W v={battery.watts} />
        </b>
      </Row>
      {dcSystemW !== null ? (
        <Row>
          <span>DC system</span>
          <b>
            <W v={dcSystemW} />
          </b>
        </Row>
      ) : null}
    </PCard>
  );
}

function SolarCard({ model }) {
  const { solar, flows } = model;
  return (
    <PCard $accent={theme.colors.accent.light} aria-label="Solar yield">
      <CardTitle>
        <span>Solar Yield</span>
      </CardTitle>
      {solar.watts === null ? (
        <Empty>No solar data</Empty>
      ) : (
        <>
          <Watts w={solar.watts} arrow={arrowFor(flows.solarToBattery.dir, 'right', 'left')} />
          {solar.yieldTodayKwh !== null ? (
            <Row>
              <span>Today</span>
              <b><N v={solar.yieldTodayKwh} d={2} u="kWh" /></b>
            </Row>
          ) : null}
          {solar.controllers.length > 1
            ? solar.controllers.map((c) => (
                <Row key={c.id}>
                  <span>{c.name}</span>
                  <b>
                    <W v={c.watts} />
                    {c.state ? ` · ${c.state}` : ''}
                  </b>
                </Row>
              ))
            : solar.controllers[0]?.state
              ? (
                <Row>
                  <span>State</span>
                  <b>{solar.controllers[0].state}</b>
                </Row>
              )
              : null}
        </>
      )}
    </PCard>
  );
}

/* -------------------------------------------------------------- page */

const configKey = (vid) => `boatwise.powerflow.${vid}`;

function loadConfig(vid) {
  if (!vid) return {};
  try {
    return JSON.parse(localStorage.getItem(configKey(vid)) || '{}') || {};
  } catch {
    return {};
  }
}

const PowerFlowPage = () => {
  const { vesselId: vesselIdParam } = useParams();
  const navigate = useNavigate();
  const paramId = vesselIdParam ? parseInt(vesselIdParam, 10) : null;

  const {
    vesselId,
    liveData,
    signalKFlat,
    vesselStreamConnected,
    loadSignalKData,
  } = useSignalkDashboardData(paramId);

  const [vessel, setVessel] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [showSettings, setShowSettings] = useState(false);
  const [config, setConfig] = useState({});
  const [hints, setHints] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!vesselId) return;
    setConfig(loadConfig(vesselId));
    api
      .get(`/api/vessels/${vesselId}`)
      .then((r) => setVessel(r.data?.vessel || r.data))
      .catch(() => setVessel(null));
  }, [vesselId]);

  // Follow the user's own default dashboard: which bank is "House", which battery/charger is "Start".
  useEffect(() => {
    if (!vesselId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const def = await api.get('/api/dashboards/default');
        if (cancelled || Number(def.data?.vessel_id) !== Number(vesselId)) return;
        const full = await api.get(`/api/dashboards/${def.data.id}`);
        if (cancelled) return;
        const widgets = (full.data?.sections || []).flatMap((sec) =>
          (sec.widgets || []).map((w) => ({
            widget_name: w.widget_name,
            data_path: w.data_path,
            section_name: sec.section_name || sec.name,
          }))
        );
        setHints(deriveDashboardHints(widgets));
      } catch {
        if (!cancelled) setHints(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vesselId]);

  const updateConfig = useCallback(
    (patch) => {
      setConfig((prev) => {
        const next = { ...prev, ...patch };
        try {
          localStorage.setItem(configKey(vesselId), JSON.stringify(next));
        } catch {
          /* storage unavailable */
        }
        return next;
      });
    },
    [vesselId]
  );

  const demoFlat = useMemo(() => buildDemoPowerFlat(), []);

  const { model: liveModel, isDemo } = useMemo(() => {
    const paths = listPowerPaths(liveData, signalKFlat);
    if (hasPowerData(paths)) {
      const read = createPowerReader({ liveData, signalKFlat, vesselStreamConnected, now });
      return { model: buildPowerFlowModel(paths, read, { ...config, hints }), isDemo: false };
    }
    // Priority: live → database → demo. No power data from either → clearly labelled demo sample.
    const demoPaths = Object.keys(demoFlat);
    const read = createPowerReader({ liveData: {}, signalKFlat: demoFlat, vesselStreamConnected: false, now });
    return { model: buildPowerFlowModel(demoPaths, read, config), isDemo: true };
  }, [liveData, signalKFlat, vesselStreamConnected, now, config, hints, demoFlat]);

  // Calm the UI: sample at most every 1.5s, then apply hysteresis to flow directions.
  const throttledModel = useThrottledValue(liveModel, 1500);
  const model = useStableFlows(throttledModel);

  const clock = new Date(now).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const lastUpdate = model.lastUpdate
    ? new Date(model.lastUpdate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  const sourceBadge = isDemo
    ? { label: 'Demo data', bg: 'rgba(245,158,11,0.15)', color: theme.colors.warning.light, border: 'rgba(245,158,11,0.4)' }
    : model.source === 'live'
      ? { label: 'Live', bg: 'rgba(16,185,129,0.15)', color: theme.colors.success.light, border: 'rgba(16,185,129,0.35)' }
      : model.source === 'stale'
        ? { label: 'Stale', bg: 'rgba(245,158,11,0.15)', color: theme.colors.warning.light, border: 'rgba(245,158,11,0.4)' }
        : { label: 'Last known (database)', bg: 'rgba(239,68,68,0.12)', color: theme.colors.error.light, border: 'rgba(239,68,68,0.35)' };

  const inverters = model.inverters;

  return (
    <Page>
      <Header>
        <div>
          <Btn type="button" onClick={() => navigate(-1)} style={{ marginBottom: '0.5rem' }}>
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </Btn>
          <Title>
            <BoltIcon className="w-6 h-6" style={{ color: theme.colors.secondary.light }} />
            Power Flow
          </Title>
          <Subtitle>
            Shore, inverter, battery and solar for {vessel?.vessel_name || 'your vessel'}
            {lastUpdate ? ` · last reading ${lastUpdate}` : ''}
          </Subtitle>
        </div>
        <Toolbar>
          <Badge $bg={sourceBadge.bg} $color={sourceBadge.color} $border={sourceBadge.border}>
            <SignalIcon className="w-3.5 h-3.5" />
            {sourceBadge.label}
          </Badge>
          <Badge>{clock}</Badge>
          <Btn type="button" onClick={() => setShowSettings((s) => !s)} data-active={showSettings}>
            <Cog6ToothIcon className="w-4 h-4" />
            Settings
          </Btn>
          <Btn type="button" onClick={() => loadSignalKData()}>
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </Btn>
        </Toolbar>
      </Header>

      {isDemo ? (
        <Banner>
          No power data from this vessel yet — showing a demo sample. Connect a Victron Cerbo GX via the BoatWise
          SignalK plugin to see your real system.
        </Banner>
      ) : null}

      {showSettings ? (
        <Panel>
          <label>
            Battery bank
            <select
              value={config.batteryId || ''}
              onChange={(e) => updateConfig({ batteryId: e.target.value || undefined })}
            >
              <option value="">{hints?.house ? 'From my dashboard' : 'Auto-detect'}</option>
              {model.batteryChoices.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                  {c.hasSoc ? '' : ' (no SOC)'}
                </option>
              ))}
            </select>
          </label>
          <label>
            <input
              type="checkbox"
              checked={Boolean(config.swapAc)}
              onChange={(e) => updateConfig({ swapAc: e.target.checked })}
              style={{ marginRight: '0.4rem' }}
            />
            Swap AC input / output phase readings
          </label>
        </Panel>
      ) : null}

      <Board>
        <Area $area="grid">
          <GridCard model={model} />
        </Area>
        <FlowLine area="c1" flow={model.flows.gridToInverter} />
        <Area $area="inv">
          {inverters.length === 0 ? (
            <PCard $accent={theme.colors.secondary.main}>
              <CardTitle>
                <span>Inverter / Charger</span>
              </CardTitle>
              <Empty>No inverter data</Empty>
            </PCard>
          ) : (
            inverters.map((inv) => <InverterCard key={inv.id} inv={inv} />)
          )}
        </Area>
        <FlowLine area="c2" flow={model.flows.inverterToLoads} />
        <Area $area="loads">
          <LoadsCard model={model} />
        </Area>

        <FlowLine area="v1" vertical flow={model.flows.inverterToBattery} />

        <Area $area="solar">
          <SolarCard model={model} />
        </Area>
        <FlowLine area="c3" flow={model.flows.solarToBattery} />
        <Area $area="batt">
          <BatteryCard model={model} />
        </Area>
        <Area $area="health">
          <HealthCard model={model} />
        </Area>
        <Area $area="start">
          <StarterCard model={model} />
        </Area>
        <Area $area="chg">
          <ChargersCard model={model} />
        </Area>
      </Board>
    </Page>
  );
};

export default PowerFlowPage;
